import React, { useState, useRef } from 'react';
import Webcam from 'react-webcam';
import TabPageLayout, { PageLoader, EmptyState } from './TabPageLayout';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { 
  Plus,
  Receipt,
  Camera,
  Upload,
  ChevronLeft,
  ChevronRight,
  Trash2,
  Pencil,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { orgApi } from '../utils/apiV2';
import { useExpenses, useInvalidateFinancials } from '../hooks/useFinancials';
import { useOperatorLocale } from '../hooks/useOperatorLocale';
import { ExportMenu } from './ExportMenu';
import { DateRangeControl } from './DateRangeControl';
import { BACKEND_URL } from '../config/api';

// Expense categories
const EXPENSE_CATEGORIES = [
  { id: 'fuel', label: 'Fuel', color: 'bg-amber-100 text-amber-700' },
  { id: 'maintenance', label: 'Maintenance', color: 'bg-blue-100 text-blue-700' },
  { id: 'insurance', label: 'Insurance', color: 'bg-purple-100 text-purple-700' },
  { id: 'supplies', label: 'Supplies', color: 'bg-green-100 text-green-700' },
  { id: 'labor', label: 'Labor', color: 'bg-pink-100 text-pink-700' },
  { id: 'rent', label: 'Rent', color: 'bg-indigo-100 text-indigo-700' },
  { id: 'marketing', label: 'Marketing', color: 'bg-cyan-100 text-cyan-700' },
  { id: 'utilities', label: 'Utilities', color: 'bg-amber-100 text-amber-700' },
  { id: 'other', label: 'Other', color: 'bg-gray-100 text-gray-700' }
];

// Format currency
const formatCurrency = (amount, currency = 'USD') => {
  const symbols = { USD: '$', AED: 'AED ', EUR: '€', GBP: '£' };
  const symbol = symbols[currency] || '$';
  return `${symbol}${Math.abs(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const ExpensesTab = () => {
  // Date range
  const getDefaultDateRange = () => {
    const now = new Date();
    return {
      startDate: `${now.getFullYear()}-01-01`,
      endDate: `${now.getFullYear()}-12-31`
    };
  };
  
  const [dateRange, setDateRange] = useState(getDefaultDateRange);
  const [page, setPage] = useState(1);
  const ITEMS_PER_PAGE = 15;
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showScanModal, setShowScanModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [scanLoading, setScanLoading] = useState(false);
  const [cameraActive, setCameraActive] = useState(false);
  
  // Refs
  const webcamRef = useRef(null);
  const fileInputRef = useRef(null);
  
  // Form state
  const [formData, setFormData] = useState({
    description: '',
    amount: '',
    category: 'other',
    expense_date: new Date().toISOString().split('T')[0],
    notes: ''
  });
  
  // Currency from the operator's country (single source of truth)
  const { currencyCode: currency } = useOperatorLocale();
  const orgId = localStorage.getItem('orgId');
  
  // Fetch expenses via shared hook — canonical key shared with P&L so they
  // dedupe + stay in sync.
  const { expenses, isLoading, isError: error, refresh: mutate } = useExpenses(
    dateRange.startDate,
    dateRange.endDate
  );
  // Refresh every financial surface (Dashboard, Revenue, P&L) after a write
  const invalidateFinancials = useInvalidateFinancials();
  
  // Calculate total
  const totalExpenses = expenses.reduce((sum, e) => sum + (e.amount || 0), 0);
  
  // Pagination
  const totalPages = Math.ceil(expenses.length / ITEMS_PER_PAGE);
  const paginatedExpenses = expenses.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);
  
  // Reset form
  const resetForm = () => {
    setFormData({
      description: '',
      amount: '',
      category: 'other',
      expense_date: new Date().toISOString().split('T')[0],
      notes: ''
    });
    setEditingExpense(null);
  };
  
  // Open edit modal
  const openEditModal = (expense) => {
    setEditingExpense(expense);
    setFormData({
      description: expense.title || expense.description || '',
      amount: expense.amount?.toString() || '',
      category: expense.category || 'other',
      expense_date: (expense.date || expense.expense_date)?.split('T')[0] || new Date().toISOString().split('T')[0],
      notes: expense.notes || ''
    });
    setShowAddModal(true);
  };
  
  // Save expense
  const handleSave = async () => {
    if (!formData.description || !formData.amount) {
      toast.error('Please fill in description and amount');
      return;
    }
    
    setIsSubmitting(true);
    try {
      const payload = {
        title: formData.description,
        amount: parseFloat(formData.amount),
        date: formData.expense_date,
        category: formData.category,
        notes: formData.notes,
        currency: currency
      };
      
      if (editingExpense) {
        await orgApi.updateExpense(editingExpense.id, payload);
        toast.success('Expense updated!');
      } else {
        await orgApi.createExpense(payload);
        toast.success('Expense added!');
      }
      
      mutate();
      invalidateFinancials();
      setShowAddModal(false);
      resetForm();
    } catch (err) {
      console.error('Save expense error:', err);
      toast.error('Failed to save expense');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Delete expense
  const handleDelete = async (expense) => {
    if (!confirm('Delete this expense?')) return;
    
    try {
      await orgApi.deleteExpense(expense.id);
      toast.success('Expense deleted');
      mutate();
      invalidateFinancials();
    } catch (err) {
      console.error('Delete expense error:', err);
      toast.error('Failed to delete expense');
    }
  };
  
  // Receipt scanning
  const captureReceipt = async () => {
    if (!webcamRef.current) return;
    
    setScanLoading(true);
    try {
      const imageSrc = webcamRef.current.getScreenshot();
      await processReceiptImage(imageSrc);
    } catch (err) {
      console.error('Capture error:', err);
      toast.error('Failed to capture image');
    } finally {
      setScanLoading(false);
      setCameraActive(false);
    }
  };
  
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setScanLoading(true);
    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        await processReceiptImage(reader.result);
      };
      reader.readAsDataURL(file);
    } catch (err) {
      console.error('Upload error:', err);
      toast.error('Failed to process receipt');
    }
  };
  
  const processReceiptImage = async (imageData) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/expenses/scan-receipt`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('jwtToken')}`
        },
        body: JSON.stringify({ image: imageData, organization_id: orgId })
      });
      
      if (!response.ok) throw new Error('Scan failed');
      
      const result = await response.json();
      
      if (result.success && result.data) {
        setFormData({
          description: result.data.vendor || result.data.description || '',
          amount: result.data.total?.toString() || '',
          category: result.data.category || 'other',
          expense_date: result.data.date || new Date().toISOString().split('T')[0],
          notes: 'Scanned from receipt'
        });
        setShowScanModal(false);
        setShowAddModal(true);
        toast.success('Receipt scanned! Review and save.');
      } else {
        toast.error('Could not parse receipt. Try manual entry.');
      }
    } catch (err) {
      console.error('Scan error:', err);
      toast.error('Failed to scan receipt');
    } finally {
      setScanLoading(false);
    }
  };
  
  // Export functions
  const exportCSV = () => {
    if (expenses.length === 0) {
      toast.error('No data to export');
      return;
    }
    
    const headers = ['Date', 'Description', 'Category', 'Amount', 'Notes'];
    const rows = expenses.map(e => [
      e.date || e.expense_date || '',
      (e.title || e.description || '').replace(/,/g, ' '),
      e.category || '',
      e.amount || 0,
      (e.notes || '').replace(/,/g, ';')
    ]);
    
    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `expenses_${dateRange.startDate}_to_${dateRange.endDate}.csv`;
    a.click();
    toast.success('Expenses exported as CSV!');
  };
  
  const exportJSON = () => {
    if (expenses.length === 0) {
      toast.error('No data to export');
      return;
    }
    
    const exportData = {
      period: { startDate: dateRange.startDate, endDate: dateRange.endDate },
      summary: { totalExpenses: totalExpenses, expenseCount: expenses.length },
      expenses: expenses.map(e => ({
        date: e.date || e.expense_date,
        description: e.title || e.description,
        category: e.category,
        amount: e.amount,
        notes: e.notes
      }))
    };
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `expenses_${dateRange.startDate}_to_${dateRange.endDate}.json`;
    a.click();
    toast.success('Expenses exported as JSON!');
  };
  
  const exportPDF = () => {
    if (expenses.length === 0) {
      toast.error('No data to export');
      return;
    }
    
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Expense Report</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { color: #1a1a1a; border-bottom: 2px solid #ef4444; padding-bottom: 10px; }
          .summary { background: #fef2f2; padding: 15px; border-radius: 8px; margin: 20px 0; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th { background: #f9fafb; text-align: left; padding: 12px; border-bottom: 2px solid #e5e7eb; }
          td { padding: 12px; border-bottom: 1px solid #e5e7eb; }
          .amount { color: #ef4444; text-align: right; }
          .category { padding: 4px 8px; border-radius: 12px; font-size: 12px; background: #f3f4f6; }
        </style>
      </head>
      <body>
        <h1>Expense Report</h1>
        <p>Period: ${dateRange.startDate} to ${dateRange.endDate}</p>
        <div class="summary">
          <strong>Total Expenses:</strong> ${formatCurrency(totalExpenses, currency)}<br>
          <strong>Items:</strong> ${expenses.length}
        </div>
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Description</th>
              <th>Category</th>
              <th style="text-align:right">Amount</th>
            </tr>
          </thead>
          <tbody>
            ${expenses.map(e => {
              const cat = getCategoryInfo(e.category);
              return `
                <tr>
                  <td>${formatDate(e.date || e.expense_date)}</td>
                  <td>${e.title || e.description || '—'}</td>
                  <td><span class="category">${cat.label}</span></td>
                  <td class="amount">${formatCurrency(e.amount, currency)}</td>
                </tr>
              `;
            }).join('')}
          </tbody>
        </table>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
    toast.success('Opening print dialog for PDF...');
  };
  
  // Get category info
  const getCategoryInfo = (categoryId) => {
    return EXPENSE_CATEGORIES.find(c => c.id === categoryId) || EXPENSE_CATEGORIES.find(c => c.id === 'other');
  };
  
  // Error handling
  if (error) {
    toast.error('Failed to load expenses');
  }
  
  if (isLoading) {
    return (
      <TabPageLayout title="Expenses" subtitle="Track your costs">
        <PageLoader />
      </TabPageLayout>
    );
  }
  
  return (
    <TabPageLayout 
      title="Expenses" 
      subtitle="Track your costs"
      actions={
        <div className="flex gap-2">
          <Button onClick={() => setShowScanModal(true)} variant="ghost" className="gap-2 h-10 text-gray-600 hover:text-gray-900">
            <Camera className="w-4 h-4" />
            <span className="hidden sm:inline">Scan Receipt</span>
          </Button>
          <ExportMenu onExport={(f) => ({ csv: exportCSV, pdf: exportPDF, json: exportJSON }[f]())} />
          <Button onClick={() => { resetForm(); setShowAddModal(true); }} className="gap-2 h-10 bg-blue-600 hover:bg-blue-700 text-white">
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Add Expense</span>
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Date Range Picker + Total */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <DateRangeControl
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            onStartChange={(v) => setDateRange({ ...dateRange, startDate: v })}
            onEndChange={(v) => setDateRange({ ...dateRange, endDate: v })}
          />
          
          {/* Total Expenses Badge */}
          <div className="px-4 py-2 rounded-full bg-red-50">
            <span className="text-lg font-medium text-red-600">
              {formatCurrency(totalExpenses, currency)}
            </span>
            <span className="text-sm ml-2 text-red-500/70">
              Total Expenses
            </span>
          </div>
        </div>
        
        {/* Expenses Table */}
        <div className="card-soft overflow-hidden">
          {expenses.length === 0 ? (
            <EmptyState
              icon={<Receipt className="w-10 h-10" />}
              title="No expenses yet"
              description="Add your first expense to start tracking"
              action={
                <Button onClick={() => { resetForm(); setShowAddModal(true); }} className="gap-2 bg-blue-600 hover:bg-blue-700 text-white h-10">
                  <Plus className="w-4 h-4" />
                  Add Expense
                </Button>
              }
            />
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[640px]">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                      <th className="px-4 py-3 w-20"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {paginatedExpenses.map((expense) => {
                      const cat = getCategoryInfo(expense.category);
                      return (
                        <tr key={expense.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-4 py-3 text-gray-900">{formatDate(expense.date || expense.expense_date)}</td>
                          <td className="px-4 py-3 text-gray-900">{expense.title || expense.description || '—'}</td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded-full text-xs ${cat.color}`}>
                              {cat.label}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right text-red-600">
                            {formatCurrency(expense.amount, currency)}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex justify-end gap-1">
                              <button
                                onClick={() => openEditModal(expense)}
                                className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-blue-600"
                                aria-label="Edit expense"
                              >
                                <Pencil className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDelete(expense)}
                                className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-red-600"
                                aria-label="Delete expense"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 bg-gray-50">
                  <p className="text-sm text-gray-600">
                    Showing {((page - 1) * ITEMS_PER_PAGE) + 1} - {Math.min(page * ITEMS_PER_PAGE, expenses.length)} of {expenses.length}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="h-9"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-sm text-gray-600">
                      Page {page} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      className="h-9"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      
      {/* Add/Edit Expense Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingExpense ? 'Edit Expense' : 'Add Expense'}</DialogTitle>
            <DialogDescription>
              {editingExpense ? 'Update expense details' : 'Enter expense details'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Description <span className="text-red-500">*</span></label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="e.g., Office supplies"
                className="mt-1"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Amount <span className="text-red-500">*</span></label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  placeholder="0.00"
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Date</label>
                <Input
                  type="date"
                  value={formData.expense_date}
                  onChange={(e) => setFormData({ ...formData, expense_date: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700">Category</label>
              <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {EXPENSE_CATEGORIES.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700">Notes</label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Optional notes..."
                rows={2}
                className="mt-1"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAddModal(false); resetForm(); }} className="h-10">
              Cancel
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={isSubmitting}
              className="h-10 bg-blue-600 hover:bg-blue-700 text-white"
            >
              {isSubmitting ? (
                <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Saving...</>
              ) : (
                editingExpense ? 'Update' : 'Add Expense'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Scan Receipt Modal */}
      <Dialog open={showScanModal} onOpenChange={setShowScanModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Scan Receipt</DialogTitle>
            <DialogDescription>
              Take a photo or upload an image of your receipt
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            {cameraActive ? (
              <div className="space-y-4">
                <div className="rounded-xl overflow-hidden bg-black">
                  <Webcam
                    ref={webcamRef}
                    screenshotFormat="image/jpeg"
                    className="w-full"
                    videoConstraints={{ facingMode: 'environment' }}
                  />
                </div>
                <div className="flex gap-2">
                  <Button 
                    onClick={() => setCameraActive(false)} 
                    variant="outline" 
                    className="flex-1 h-10"
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={captureReceipt} 
                    disabled={scanLoading}
                    className="flex-1 h-10 bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    {scanLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Capture'}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => setCameraActive(true)}
                  className="p-6 border-2 border-dashed border-gray-200 rounded-xl hover:border-blue-400 hover:bg-blue-50 transition-colors text-center"
                >
                  <Camera className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                  <p className="text-sm font-medium text-gray-700">Take Photo</p>
                </button>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="p-6 border-2 border-dashed border-gray-200 rounded-xl hover:border-blue-400 hover:bg-blue-50 transition-colors text-center"
                >
                  <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                  <p className="text-sm font-medium text-gray-700">Upload Image</p>
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="hidden"
                />
              </div>
            )}
            
            {scanLoading && !cameraActive && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                <span className="ml-3 text-gray-600">Processing receipt...</span>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </TabPageLayout>
  );
};

export default ExpensesTab;
